/** Members, invitations and client orgs (api/openapi.yaml Invitations, Members, Platform; SPEC §17.5). */
import { z } from 'zod';
import { OrgRole, ProjectRole, slug, uuid } from './common.js';

/** Lower-cased and trimmed; the same rule the database uses for `users.email`. */
export const email = z.string().trim().toLowerCase().max(254).regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'a valid email address');

/** Org roles an invitation or a role change may set: `owner` is never granted through the API. */
export const AssignableOrgRole = OrgRole.exclude(['owner']);
export type AssignableOrgRole = z.infer<typeof AssignableOrgRole>;

/** `InvitationCreateRequest`. */
export const InvitationCreateRequest = z.object({
  email,
  name: z.string().trim().min(1).max(120).optional(),
  company: z.string().trim().min(1).max(120).optional(),
  orgRole: AssignableOrgRole,
  projectRoles: z.array(z.object({ projectId: uuid, role: ProjectRole })).max(50).default([]),
  expiresAt: z.string().datetime({ offset: true }).optional(),
  message: z.string().trim().max(1000).optional(),
});
export type InvitationCreateRequest = z.infer<typeof InvitationCreateRequest>;

/**
 * What became of an invitation's email (SPEC §17.5): `sent` (Hangar's own email went out), `sign_in_sent` (the
 * invitation created the person's sign-in, or renewed one never used, and the sign-in email with the temporary password
 * is the invitation: Hangar sends nothing itself), `failed` (nothing went out: resend it) or `bounced` (the address
 * bounced permanently or the recipient complained: check it, then resend).
 */
export const InvitationEmailStatus = z.enum(['sent', 'sign_in_sent', 'failed', 'bounced']);
export type InvitationEmailStatus = z.infer<typeof InvitationEmailStatus>;

/** `InvitationAcceptRequest`: the token of the link (`#token=` in the console URL). */
export const InvitationAcceptRequest = z.object({ token: z.string().regex(/^hgr_inv_[A-Za-z0-9_-]{20,100}$/, 'not an invitation token') });
export type InvitationAcceptRequest = z.infer<typeof InvitationAcceptRequest>;

/** `MemberUpdateRequest` (merge patch): the org role and/or the access end date. */
export const MemberUpdateRequest = z.object({
  role: AssignableOrgRole.optional(),
  expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
}).refine((v) => v.role !== undefined || v.expiresAt !== undefined, 'nothing to change');
export type MemberUpdateRequest = z.infer<typeof MemberUpdateRequest>;

/** `ProjectMembersReplaceRequest`: each entry names a member by `userId` or `email`. */
export const ProjectMembersReplaceRequest = z.object({
  members: z.array(z.object({
    userId: uuid.optional(),
    email: email.optional(),
    role: ProjectRole,
    expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
  }).refine((m) => (m.userId === undefined) !== (m.email === undefined), 'exactly one of userId or email')).max(500),
});
export type ProjectMembersReplaceRequest = z.infer<typeof ProjectMembersReplaceRequest>;

/** `PlatformOrgCreateRequest`: a client org and its first admin (platform operators only). */
export const PlatformOrgCreateRequest = z.object({
  name: z.string().trim().min(1).max(120),
  slug,
  /** The org's email domains (who is internal); the first admin's domain when omitted. */
  emailDomains: z.array(z.string().trim().toLowerCase().regex(/^(?=.{3,253}$)[a-z0-9-]+(\.[a-z0-9-]+)+$/, 'a domain such as example.com')).max(10).optional(),
  firstAdmin: z.object({ email, name: z.string().trim().min(1).max(120).optional() }),
  message: z.string().trim().max(1000).optional(),
});
export type PlatformOrgCreateRequest = z.infer<typeof PlatformOrgCreateRequest>;

/** Client lifecycle (SPEC decision 26, §17.6): what an operator may change, and how a client is suspended or deleted. */
const domainList = z.array(z.string().trim().toLowerCase().regex(/^(?=.{3,253}$)[a-z0-9-]+(\.[a-z0-9-]+)+$/, 'a domain such as example.com')).min(1).max(10);

/** `PATCH /platform/orgs/{orgId}` (merge patch): the name and/or the email domains; the slug never changes. */
export const PlatformOrgUpdateRequest = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  emailDomains: domainList.optional(),
}).refine((v) => v.name !== undefined || v.emailDomains !== undefined, 'nothing to change');
export type PlatformOrgUpdateRequest = z.infer<typeof PlatformOrgUpdateRequest>;

/** `POST /platform/orgs/{orgId}/invitations`: an admin or a member in the client's domains (guests stay the client's call). */
export const PlatformInvitationCreateRequest = z.object({
  email,
  name: z.string().trim().min(1).max(120).optional(),
  orgRole: z.enum(['admin', 'member']),
  message: z.string().trim().max(1000).optional(),
});
export type PlatformInvitationCreateRequest = z.infer<typeof PlatformInvitationCreateRequest>;

/** `POST /platform/orgs/{orgId}/suspend`: why, shown to operators (never to the client). */
export const PlatformOrgSuspendRequest = z.object({ reason: z.string().trim().min(1).max(500) });
export type PlatformOrgSuspendRequest = z.infer<typeof PlatformOrgSuspendRequest>;

/** `POST /platform/orgs/{orgId}/deletion`: the client's slug, typed by the operator. */
export const PlatformOrgDeletionRequest = z.object({ confirmSlug: slug });
export type PlatformOrgDeletionRequest = z.infer<typeof PlatformOrgDeletionRequest>;

/** Where a client stands: `deleting` while a deletion runs, `deletion_failed` when a step failed (run it again). */
export const ClientOrgStatus = z.enum(['active', 'suspended', 'deleting', 'deletion_failed']);
export type ClientOrgStatus = z.infer<typeof ClientOrgStatus>;

/** The steps of a client's deletion, in order. */
export const ClientDeletionStepKey = z.enum(['providers', 'env_values', 'storage', 'database']);
export type ClientDeletionStepKey = z.infer<typeof ClientDeletionStepKey>;

/** A provider resource Hangar cannot remove through an API: what it is and where to remove it by hand. */
export const ManualRemoval = z.object({ what: z.string(), where: z.string() });
export type ManualRemoval = z.infer<typeof ManualRemoval>;

export const ClientDeletionStep = z.object({
  key: ClientDeletionStepKey,
  status: z.enum(['pending', 'done', 'failed']),
  message: z.string().optional(),
  manual: z.array(ManualRemoval).default([]),
  at: z.string().optional(),
});
export type ClientDeletionStep = z.infer<typeof ClientDeletionStep>;
